import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  useParams,
  useHistory,
} from "react-router-dom/cjs/react-router-dom.min";
import {
  FaShoppingBasket,
  FaTrash,
  FaMapMarkerAlt,
  FaChevronUp,
} from "react-icons/fa";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Logo from "../../assets/logo/new_logo_colored2.png";
import { useCart } from "../../subjects/CartContext";
import {
  useProduce,
  useProductTypes,
  useWarehouses,
} from "../../hooks/useProducts";
import { thousands_separators } from "../../utils/formatCurrency";
import {
  BranchSelectionModal,
  SELECTED_BRANCH_KEY,
} from "../../components/modal/branchModal";
import { EmptyStateComponent } from "../../components/modal/emptyState";
import {
  ACCENT,
  ACCENT_TINT,
  TEAL,
  INK,
  MUTED,
  LINE,
  CookingMethodSelector,
  getMethodsForProduct,
} from "../products/Products";
import ExpressProductCard from "./ExpressProductCard";

const KIOSK_IDLE_RESET_MS = 3 * 60 * 1000;
const slugify = (name) =>
  (name || "").toLowerCase().trim().replace(/\s+/g, "-");

const Express = () => {
  const { hub } = useParams();
  const history = useHistory();
  const [selectedFilter, setSelectedFilter] = useState("");
  const [branchResolved, setBranchResolved] = useState(false);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);

  const { data: warehouses } = useWarehouses();
  const { data: products } = useProduce(selectedFilter);
  const { data: product_types } = useProductTypes();
  const {
    items,
    setItems,
    getTotalPrice,
    removeItem,
    getItemsCount,
    setCookingMethodQuantity,
    cookingMethodCosts,
    getCookingFee,
    setIsSelfCollection,
  } = useCart();

  useEffect(() => {
    setIsSelfCollection(true);
  }, [setIsSelfCollection]);

  useEffect(() => {
    if (branchResolved || !warehouses?.length) return;
    if (hub) {
      const match = warehouses.find((w) =>
        slugify(w.name).includes(hub.toLowerCase()),
      );
      console.log("Matched branch from URL:", match);
      if (match) {
        localStorage.setItem(SELECTED_BRANCH_KEY, JSON.stringify(match));
        setBranchResolved(true);
        return;
      }
    }
    const saved = localStorage.getItem(SELECTED_BRANCH_KEY);
    if (saved) setBranchResolved(true);
    else setModalIsOpen(true);
  }, [hub, warehouses, branchResolved]);

  const currentBranch = useMemo(() => {
    try {
      const saved = localStorage.getItem(SELECTED_BRANCH_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  }, [branchResolved, modalIsOpen]);

  useEffect(() => {
    let timer;
    const reset = () => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        if (items.length) setItems([]);
      }, KIOSK_IDLE_RESET_MS);
    };
    ["click", "keydown", "touchstart"].forEach((evt) =>
      document.addEventListener(evt, reset),
    );
    reset();
    return () => {
      clearTimeout(timer);
      ["click", "keydown", "touchstart"].forEach((evt) =>
        document.removeEventListener(evt, reset),
      );
    };
  }, [items, setItems]);

  const groupedCartItems = useMemo(() => {
    const map = new Map();
    items.forEach((item) => {
      const key = item.product.id;
      if (!map.has(key)) {
        map.set(key, {
          product: item.product,
          totalQty: 0,
          quantitiesByMethod: {},
          totalPrice: 0,
        });
      }
      const group = map.get(key);
      group.totalQty += item.qty;
      const methodKey = item.cooking_method || "none";
      group.quantitiesByMethod[methodKey] =
        (group.quantitiesByMethod[methodKey] || 0) + item.qty;
      group.totalPrice += item.discountedTotal || item.totalPrice;
    });
    return Array.from(map.values());
  }, [items]);

  if (!currentBranch) {
    return (
      <BranchSelectionModal
        isOpen={modalIsOpen}
        onRequestClose={() => setModalIsOpen(false)}
        onSelectBranch={() => setBranchResolved(true)}
      />
    );
  }

  return (
    <Page>
      <Hero>
        <HeroTop>
          <LogoBadge>
            <img src={Logo} alt="Markiti" />
          </LogoBadge>
          <BranchPill onClick={() => setModalIsOpen(true)}>
            <FaMapMarkerAlt size={12} />
            {currentBranch.name}
          </BranchPill>
        </HeroTop>
        <HeroTitle>Markiti Express</HeroTitle>
      </Hero>

      <Body>
        <Chips>
          <Chip
            $active={selectedFilter === ""}
            onClick={() => setSelectedFilter("")}
          >
            All
          </Chip>
          {product_types
            ?.filter((type) => type.id !== 4)
            ?.map((type) => (
              <Tippy content={type?.description} key={type.id}>
                <Chip
                  $active={selectedFilter === type.id}
                  onClick={() => setSelectedFilter(type.id)}
                >
                  {type.name}
                </Chip>
              </Tippy>
            ))}
        </Chips>

        {products?.length > 0 ? (
          <ProductGrid>
            {products
              ?.filter((prod) => !prod?.name.includes("Byproducts"))
              ?.map((item) => (
                <ExpressProductCard product={item} key={item.id} />
              ))}
          </ProductGrid>
        ) : (
          <EmptyStateComponent />
        )}
      </Body>

      {items.length > 0 && (
        <>
          <FloatingBar
            $liftedByCart={cartOpen}
            onClick={() => setCartOpen(true)}
          >
            <BasketIcon>
              <FaShoppingBasket size={16} />
              <Count>{getItemsCount()}</Count>
            </BasketIcon>
            <BarLabel>View basket</BarLabel>
            <BarTotal>KSh {thousands_separators(getTotalPrice())}</BarTotal>
          </FloatingBar>

          <Backdrop $open={cartOpen} onClick={() => setCartOpen(false)} />
          <CartSheet $open={cartOpen}>
            <SheetHandle onClick={() => setCartOpen(false)}>
              <span />
            </SheetHandle>
            <SheetHead>
              <strong>
                {getItemsCount()} item{getItemsCount() === 1 ? "" : "s"}
              </strong>
              <CloseChevron onClick={() => setCartOpen(false)}>
                <FaChevronUp
                  style={{ transform: "rotate(180deg)" }}
                  size={14}
                />
              </CloseChevron>
            </SheetHead>
            <SheetScroll>
              {groupedCartItems.map((group) => {
                const resolvedMethods =
                  group.product?.cooking_methods?.length > 0
                    ? group.product.cooking_methods
                    : getMethodsForProduct(group.product, cookingMethodCosts);
                const lineTotal = group.product?.discounted_price
                  ? group.totalQty * group.product.discounted_price
                  : group.totalPrice;
                return (
                  <CartRow key={group.product?.id}>
                    <CartRowTop>
                      <span>{group.product?.display_name}</span>
                      <RemoveBtn
                        onClick={() => removeItem(group.product?.id)}
                        aria-label="Remove"
                      >
                        <FaTrash size={12} />
                      </RemoveBtn>
                    </CartRowTop>
                    <CartRowMeta>
                      <span>
                        {group.totalQty} × KSh{" "}
                        {thousands_separators(group.product?.unit_value)}
                      </span>
                      <strong>
                        KSh {thousands_separators(lineTotal?.toFixed(0))}
                      </strong>
                    </CartRowMeta>
                    {resolvedMethods.length > 0 && (
                      <CookingMethodSelector
                        methods={resolvedMethods}
                        cookingMethodCosts={cookingMethodCosts}
                        totalQty={group.totalQty}
                        quantitiesByMethod={group.quantitiesByMethod}
                        onChange={(code, qty) =>
                          setCookingMethodQuantity(group.product.id, code, qty)
                        }
                      />
                    )}
                  </CartRow>
                );
              })}
            </SheetScroll>
            {(getCookingFee() > 0 || getTotalPrice() < 4000) && (
              <FeeNote>
                {getCookingFee() > 0 && (
                  <div>
                    Prep fee: KSh {thousands_separators(getCookingFee())}
                  </div>
                )}
                <FreshRow>
                  <Dot />
                  Self-collection selected — no delivery fee
                </FreshRow>
              </FeeNote>
            )}
            <CheckoutBtn onClick={() => history.push("/express-checkout")}>
              <span>Checkout</span>
              <span>KSh {thousands_separators(getTotalPrice())}</span>
            </CheckoutBtn>
          </CartSheet>
        </>
      )}
    </Page>
  );
};

export default Express;

/* ---------- visual language ----------
   Purple (ACCENT) carries all primary UI weight — header, chips, CTAs.
   Teal stays reserved for freshness/success only, per the existing
   convention in Products.jsx (the "Dot" and success screens below).
*/

const Page = styled.div`
  max-width: 640px;
  margin: 0 auto;
  min-height: 100vh;
  background: #f6f9fc;
  padding-bottom: 6rem;
`;

const Hero = styled.div`
  background: linear-gradient(135deg, #6a61c9 0%, ${ACCENT} 55%, #241c40 100%);
  border-radius: 0 0 1.5rem 1.5rem;
  padding: 1rem 1.25rem 1.15rem;
  color: #fff;
`;
const HeroTop = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const LogoBadge = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5px;
  img {
    width: 100%;
    object-fit: contain;
  }
`;
const BranchPill = styled.button`
  display: flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(255, 255, 255, 0.16);
  border: 1px solid rgba(255, 255, 255, 0.3);
  color: #fff;
  border-radius: 999px;
  padding: 0.4rem 0.75rem;
  font-weight: 700;
  font-size: 0.75rem;
  cursor: pointer;
`;
const HeroTitle = styled.h1`
  font-size: 1.3rem;
  font-weight: 800;
  margin: 0.7rem 0 0.1rem;
  letter-spacing: -0.01em;
`;
const HeroTag = styled.p`
  margin: 0;
  font-size: 0.82rem;
  opacity: 0.85;
`;

const Body = styled.div`
  padding: 1rem 1rem 0;
`;
const Chips = styled.div`
  display: flex;
  gap: 0.5rem;
  overflow-x: auto;
  padding-bottom: 1rem;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;
const Chip = styled.button`
  flex-shrink: 0;
  padding: 0.55rem 1rem;
  border-radius: 999px;
  border: none;
  background: ${(p) => (p.$active ? ACCENT : "#fff")};
  color: ${(p) => (p.$active ? "#fff" : INK)};
  font-weight: 700;
  font-size: 0.8rem;
  cursor: pointer;
  box-shadow: ${(p) =>
    p.$active ? `0 6px 16px ${ACCENT}4D` : "0 2px 8px rgba(36,28,64,0.08)"};
  transition:
    transform 0.12s ease,
    box-shadow 0.12s ease;
  &:active {
    transform: scale(0.96);
  }
`;
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 0.85rem;
  padding-bottom: 5.5rem;
`;

const FloatingBar = styled.div`
  position: fixed;
  left: 50%;
  bottom: 1rem;
  transform: translateX(-50%);
  width: calc(100% - 2rem);
  max-width: 600px;
  background: ${ACCENT};
  color: #fff;
  border-radius: 999px;
  padding: 0.85rem 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.6rem;
  box-shadow: 0 10px 30px rgba(73, 67, 149, 0.35);
  cursor: pointer;
  z-index: 20;
`;
const BasketIcon = styled.div`
  position: relative;
  display: flex;
`;
const Count = styled.span`
  position: absolute;
  top: -8px;
  right: -8px;
  background: ${TEAL};
  color: #fff;
  font-size: 0.62rem;
  font-weight: 800;
  border-radius: 50%;
  width: 16px;
  height: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const BarLabel = styled.span`
  flex: 1;
  font-weight: 700;
  font-size: 0.88rem;
`;
const BarTotal = styled.span`
  font-weight: 800;
  background: rgba(255, 255, 255, 0.18);
  padding: 0.3rem 0.7rem;
  border-radius: 999px;
  font-size: 0.85rem;
`;

const slideUp = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const Backdrop = styled.div`
  display: ${(p) => (p.$open ? "block" : "none")};
  position: fixed;
  inset: 0;
  background: rgba(20, 16, 40, 0.35);
  z-index: 29;
`;
const CartSheet = styled.div`
  display: ${(p) => (p.$open ? "flex" : "none")};
  flex-direction: column;
  position: fixed;
  left: 50%;
  bottom: 0;
  transform: translateX(-50%);
  width: 100%;
  max-width: 640px;
  max-height: 72vh;
  background: #fff;
  border-radius: 1.5rem 1.5rem 0 0;
  box-shadow: 0 -10px 40px rgba(0, 0, 0, 0.15);
  z-index: 30;
  animation: ${slideUp} 0.22s ease-out;
`;
const SheetHandle = styled.div`
  display: flex;
  justify-content: center;
  padding: 0.6rem 0 0.2rem;
  cursor: pointer;
  span {
    width: 36px;
    height: 4px;
    border-radius: 999px;
    background: ${LINE};
  }
`;
const SheetHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.3rem 1.1rem 0.6rem;
  color: ${INK};
`;
const CloseChevron = styled.button`
  background: ${ACCENT_TINT};
  border: none;
  color: ${ACCENT};
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;
const SheetScroll = styled.div`
  overflow-y: auto;
  padding: 0 1.1rem;
`;
const CartRow = styled.div`
  border-top: 1px solid ${LINE};
  padding: 0.8rem 0;
`;
const CartRowTop = styled.div`
  display: flex;
  justify-content: space-between;
  font-weight: 700;
  color: ${INK};
`;
const RemoveBtn = styled.button`
  background: none;
  border: none;
  color: #ef4444;
  cursor: pointer;
`;
const CartRowMeta = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 0.83rem;
  color: ${MUTED};
  margin-top: 0.2rem;
`;
const FeeNote = styled.div`
  padding: 0.5rem 1.1rem;
  font-size: 0.75rem;
  color: ${MUTED};
`;
const FreshRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;
const Dot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${TEAL};
  flex-shrink: 0;
`;
const CheckoutBtn = styled.button`
  margin: 0.7rem 1.1rem calc(1rem + env(safe-area-inset-bottom));
  background: linear-gradient(135deg, ${ACCENT} 0%, #2e2a63 100%);
  color: #fff;
  border: none;
  border-radius: 1rem;
  padding: 1.05rem 1.25rem;
  display: flex;
  justify-content: space-between;
  font-weight: 800;
  font-size: 1rem;
  cursor: pointer;
  box-shadow: 0 8px 24px rgba(73, 67, 149, 0.3);
`;
